import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { TrailStep } from '../ui/LevelTrail';

interface ShellContextValue {
  trail: TrailStep[];
  setTrail: (steps: TrailStep[]) => void;
}

const ShellContext = createContext<ShellContextValue>({
  trail: [],
  setTrail: () => undefined,
});

interface TrailEntry {
  /** Путь, для которого цепочка объявлена. */
  path: string;
  steps: TrailStep[];
}

export function ShellProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [entry, setEntry] = useState<TrailEntry>({ path: pathname, steps: [] });

  const setTrail = useCallback(
    (steps: TrailStep[]) => setEntry({ path: pathname, steps }),
    [pathname],
  );

  /*
   * Цепочка привязана к пути, а не к сессии оболочки. Экран, который свою
   * цепочку не объявил, обязан показать пустую шапку: чужая крошка от
   * предыдущей страницы врёт про уровень сильнее, чем её отсутствие.
   */
  const trail = entry.path === pathname ? entry.steps : [];
  const value = useMemo(() => ({ trail, setTrail }), [trail, setTrail]);
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellContextValue {
  return useContext(ShellContext);
}

/**
 * Экран объявляет свою цепочку уровней. Сериализация в ключ эффекта нужна,
 * чтобы литерал массива в теле страницы не перезапускал эффект на каждый рендер.
 */
export function usePageTrail(steps: TrailStep[]): void {
  const { setTrail } = useShell();
  const key = JSON.stringify(steps);
  useEffect(() => {
    setTrail(JSON.parse(key) as TrailStep[]);
  }, [key, setTrail]);
}
